import axios from 'axios';
import {
  ApiResponse, LoginResponse, Product, Category, Notice, Color,
  Paginated, AdminUserListItem, AdminUserDetail, LevelConfig, AppConfigItem,
  GifticonProduct, GifticonOrder, StatsSummary, DailyStat, MonthlyStat, OfferwallTransaction,
  GifticonCategory,
} from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

// Axios 인스턴스 생성
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 요청 인터셉터 - 토큰 추가
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터 - 에러 처리
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      // 인증 오류 시 로그인 페이지로 리다이렉트
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// 인증 API
export const authAPI = {
  login: (email: string, password: string) =>
    api.post<any, ApiResponse<LoginResponse>>('/auth/login', { email, password }),
  
  register: (email: string, password: string) =>
    api.post<any, ApiResponse<LoginResponse>>('/auth/register', { email, password }),
};

// 상품 API
export const productAPI = {
  getAll: (params?: { sortBy?: string; sortOrder?: 'ASC' | 'DESC' }) => {
    const searchParams = new URLSearchParams();
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder);
    
    const url = searchParams.toString() ? `/products?${searchParams.toString()}` : '/products';
    return api.get<any, ApiResponse<Product[]>>(url);
  },
  
  getById: (id: number) => api.get<any, ApiResponse<Product>>(`/products/${id}`),
  
  create: (data: Partial<Product>) => api.post<any, ApiResponse<Product>>('/products', data),
  
  update: (id: number, data: Partial<Product>) =>
    api.put<any, ApiResponse<Product>>(`/products/${id}`, data),
  
  delete: (id: number) => api.delete<any, ApiResponse<void>>(`/products/${id}`),
};

// 카테고리 API
export const categoryAPI = {
  getAll: () => api.get<any, ApiResponse<Category[]>>('/categories'),
  
  create: (data: { name: string }) => api.post<any, ApiResponse<Category>>('/categories', data),
  
  update: (id: number, data: Partial<Category>) =>
    api.put<any, ApiResponse<Category>>(`/categories/${id}`, data),
  
  delete: (id: number) => api.delete<any, ApiResponse<void>>(`/categories/${id}`),
};

// 공지사항 API
export const noticeAPI = {
  getAll: () => api.get<any, ApiResponse<Notice[]>>('/notices'),
  
  getById: (id: number) => api.get<any, ApiResponse<Notice>>(`/notices/${id}`),
  
  create: (data: { title: string; content: string }) =>
    api.post<any, ApiResponse<Notice>>('/notices', data),
  
  update: (id: number, data: Partial<Notice>) =>
    api.put<any, ApiResponse<Notice>>(`/notices/${id}`, data),
  
  delete: (id: number) => api.delete<any, ApiResponse<void>>(`/notices/${id}`),
};

// 색상 API
export const colorAPI = {
  getAll: () => api.get<any, ApiResponse<Color[]>>('/colors'),

  create: (data: { name: string; hexCode: string }) =>
    api.post<any, ApiResponse<Color>>('/colors', data),

  update: (id: number, data: Partial<Color>) =>
    api.put<any, ApiResponse<Color>>(`/colors/${id}`, data),

  delete: (id: number) => api.delete<any, ApiResponse<void>>(`/colors/${id}`),
};

// ===== 앱테크 관리자 API =====

// 유저 관리
export const userAdminAPI = {
  getAll: (params?: { search?: string; page?: number; limit?: number; suspended?: string }) => {
    const sp = new URLSearchParams();
    if (params?.search) sp.append('search', params.search);
    if (params?.page) sp.append('page', String(params.page));
    if (params?.limit) sp.append('limit', String(params.limit));
    if (params?.suspended) sp.append('suspended', params.suspended);
    const q = sp.toString();
    return api.get<any, ApiResponse<Paginated<AdminUserListItem>>>(`/admin/users${q ? `?${q}` : ''}`);
  },
  getById: (id: number) => api.get<any, ApiResponse<AdminUserDetail>>(`/admin/users/${id}`),
  adjustPoints: (id: number, data: { amount: number; type: 'grant' | 'confiscate'; memo?: string; bucket?: 'points' | 'bonus' }) =>
    api.post<any, ApiResponse<any>>(`/admin/users/${id}/points`, data),
  suspend: (id: number, data: { suspended: boolean; reason?: string }) =>
    api.post<any, ApiResponse<any>>(`/admin/users/${id}/suspend`, data),
  reset: (id: number, data: { scope: 'daily' | 'points' | 'all' }) =>
    api.post<any, ApiResponse<any>>(`/admin/users/${id}/reset`, data),
  resetDevice: (id: number) =>
    api.post<any, ApiResponse<any>>(`/admin/users/${id}/reset-device`),
  blockDevice: (deviceId: string, data: { blocked: boolean; reason?: string }) =>
    api.post<any, ApiResponse<any>>(`/admin/devices/${deviceId}/block`, data),
};

// 직급 설정 API
export const levelConfigAPI = {
  getAll: () => api.get<any, ApiResponse<LevelConfig[]>>('/admin/level-configs'),
  create: (data: Partial<LevelConfig>) => api.post<any, ApiResponse<LevelConfig>>('/admin/level-configs', data),
  update: (id: number, data: Partial<LevelConfig>) => api.put<any, ApiResponse<LevelConfig>>(`/admin/level-configs/${id}`, data),
  delete: (id: number) => api.delete<any, ApiResponse<void>>(`/admin/level-configs/${id}`),
};

// 앱 설정 API
export const appConfigAPI = {
  getAll: () => api.get<any, ApiResponse<AppConfigItem[]>>('/admin/app-configs'),
  update: (key: string, value: string) => api.put<any, ApiResponse<any>>(`/admin/app-configs/${key}`, { value }),
};

// 기프티콘 카탈로그 API
export const gifticonAPI = {
  getAll: () => api.get<any, ApiResponse<GifticonProduct[]>>('/admin/gifticons'),
  create: (data: Partial<GifticonProduct>) => api.post<any, ApiResponse<GifticonProduct>>('/admin/gifticons', data),
  update: (id: number, data: Partial<GifticonProduct>) => api.put<any, ApiResponse<GifticonProduct>>(`/admin/gifticons/${id}`, data),
  delete: (id: number) => api.delete<any, ApiResponse<void>>(`/admin/gifticons/${id}`),
  lookup: (goodsCode: string) => api.post<any, ApiResponse<{ goodsCode: string; goodsName: string; brandName: string | null; imageUrl: string | null; marketPrice: number | null }>>('/admin/gifticons/lookup', { goodsCode }),
};

// 기프티콘 카테고리 API (관리자 관리형)
export const gifticonCategoryAPI = {
  getAll: () => api.get<any, ApiResponse<GifticonCategory[]>>('/admin/gifticon-categories'),
  create: (data: { name: string; sortOrder?: number }) => api.post<any, ApiResponse<GifticonCategory>>('/admin/gifticon-categories', data),
  update: (id: number, data: Partial<GifticonCategory>) => api.put<any, ApiResponse<GifticonCategory>>(`/admin/gifticon-categories/${id}`, data),
  delete: (id: number) => api.delete<any, ApiResponse<void>>(`/admin/gifticon-categories/${id}`),
};

// 기프티콘 주문 API
export const gifticonOrderAPI = {
  getAll: (params?: { status?: string; userId?: number; page?: number; limit?: number }) => {
    const sp = new URLSearchParams();
    if (params?.status) sp.append('status', params.status);
    if (params?.userId) sp.append('userId', String(params.userId));
    if (params?.page) sp.append('page', String(params.page));
    if (params?.limit) sp.append('limit', String(params.limit));
    const q = sp.toString();
    return api.get<any, ApiResponse<Paginated<GifticonOrder>>>(`/admin/gifticon-orders${q ? `?${q}` : ''}`);
  },
  updateStatus: (id: number, status: string) => api.put<any, ApiResponse<GifticonOrder>>(`/admin/gifticon-orders/${id}`, { status }),
};

// 오퍼월 적립 API
export const offerwallAdminAPI = {
  getAll: (params?: { provider?: string; userId?: number; page?: number; limit?: number }) => {
    const sp = new URLSearchParams();
    if (params?.provider) sp.append('provider', params.provider);
    if (params?.userId) sp.append('userId', String(params.userId));
    if (params?.page) sp.append('page', String(params.page));
    if (params?.limit) sp.append('limit', String(params.limit));
    const q = sp.toString();
    return api.get<any, ApiResponse<Paginated<OfferwallTransaction>>>(`/admin/offerwall-transactions${q ? `?${q}` : ''}`);
  },
};

// 통계 API
export const statsAPI = {
  summary: (date?: string) => api.get<any, ApiResponse<StatsSummary>>(`/admin/stats/summary${date ? `?date=${date}` : ''}`),
  daily: (from: string, to: string) => api.get<any, ApiResponse<DailyStat[]>>(`/admin/stats/daily?from=${from}&to=${to}`),
  monthly: (year: string) => api.get<any, ApiResponse<MonthlyStat[]>>(`/admin/stats/monthly?year=${year}`),
}; 